import { tiptapDocToPlainText, type TiptapDoc } from '@odo/shared-ui';

/**
 * Flattens markdown to text for search, snippets and reading-time estimates.
 * Lives here rather than in resourceIndex so this module stays the one place
 * that knows about a resource field's storage format; resourceIndex re-exports
 * it for its long-standing callers.
 */
export function toPlainText(markdown: string): string {
    return (markdown ?? '')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/!\[.*?\]\(.*?\)/g, ' ')
        .replace(/\[(.*?)\]\(.*?\)/g, '$1')
        .replace(/^#+\s/gm, '')
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        .replace(/(\*|_)(.*?)\1/g, '$2')
        .replace(/`/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * FAQ answers and video descriptions are authored in the rich editor, which
 * stores a Tiptap document. The columns are plain text, so the document is held
 * as JSON in the same column the old markdown lived in.
 *
 * Rows written before the editor landed hold markdown or plain text. So every
 * read goes through `parseRichText`: a JSON document renders through the editor,
 * anything else falls back to the previous rendering. There is no migration and no flag day.
 */
export function parseRichText(value: string | null | undefined): TiptapDoc | null {
    if (!value) return null;
    const trimmed = value.trim();
    // Cheap reject before paying for JSON.parse: a document always starts as an
    // object, and no markdown answer in the library opens with a brace.
    if (!trimmed.startsWith('{')) return null;
    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (
            parsed !== null &&
            typeof parsed === 'object' &&
            (parsed as { type?: unknown }).type === 'doc' &&
            Array.isArray((parsed as { content?: unknown }).content)
        ) {
            return parsed as TiptapDoc;
        }
        return null;
    } catch {
        return null;
    }
}

export function serializeRichText(doc: TiptapDoc): string {
    return JSON.stringify(doc);
}

/** An empty document, for a field being authored from scratch. */
export function emptyRichText(): TiptapDoc {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/**
 * Loads a stored value into the editor. Legacy markdown is carried in verbatim,
 * one paragraph per line: converting it would be guesswork, and an author who
 * sees their own text can fix it in place.
 */
export function toRichTextDoc(value: string | null | undefined): TiptapDoc {
    const parsed = parseRichText(value);
    if (parsed) return parsed;

    const lines = (value ?? '').split('\n').filter((line) => line.trim().length > 0);
    if (lines.length === 0) return emptyRichText();
    return {
        type: 'doc',
        content: lines.map((line) => ({
            type: 'paragraph',
            content: [{ type: 'text', text: line }]
        }))
    };
}

/** True when the document carries no text - the editor's "empty" is a doc with
 * one empty paragraph, which is not the same as an empty string. */
export function isRichTextEmpty(doc: TiptapDoc): boolean {
    return tiptapDocToPlainText(doc).trim().length === 0;
}

/**
 * Plain text for search, snippets and clamped previews, whichever format the
 * value is stored in. Both branches feed the ⌘K index, so a document must never
 * leak its JSON keys into a search haystack.
 */
export function richTextToPlainText(value: string | null | undefined): string {
    const parsed = parseRichText(value);
    if (parsed) return tiptapDocToPlainText(parsed).replace(/\s+/g, ' ').trim();
    return toPlainText(value ?? '');
}
